import type { Row, DashboardData } from "./types";

const BASE = "/api";

export interface PlanoAcaoItem {
  descricao: string;
  tipo: string;
  responsavel: string;
  prazo_dias: number;
}

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });

export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "ERROR") {
    super(message);
    this.code = code;
  }
}

/** Baixa um arquivo da API (com o header de sessão) e dispara o download no navegador. */
async function baixarArquivo(url: string, nome: string): Promise<void> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    try {
      const b = await res.json();
      if (b?.error) msg = b.error;
    } catch {
      /* corpo não-JSON */
    }
    throw new ApiError(msg);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code);
  }
  return res.json();
}

export const api = {
  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /** Retorna { usuario, nome, cargo, isAdmin, podeExcluir } do usuário informado. */
  me: (
    usuario: string,
  ): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    /** Só administradores (cargo/flag de admin no Portal) podem excluir registros. */
    podeExcluir: boolean;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /**
   * Documentos: arquivo e fluxo de aprovação. Quem elabora e revisa anexa,
   * escolhe quem aprova e envia; quem publica cada setor vem da configuração.
   * Só então vira a versão vigente (ver server/documentos-fluxo.ts e
   * server/documentos-config.ts).
   */
  documentos: {
    /** Baixa a versão VIGENTE (envia o header de sessão). */
    download: (id: number, nome: string): Promise<void> =>
      baixarArquivo(`${BASE}/documentos/${id}/arquivo`, nome || `documento-${id}`),

    /** Baixa o arquivo de uma revisão do histórico. */
    baixarRevisao: (id: number, revisaoId: number, nome: string | null): Promise<void> =>
      baixarArquivo(`${BASE}/documentos/${id}/revisoes/${revisaoId}/arquivo`, nome || `documento-${id}`),

    /** Revisões, histórico e o que a pessoa logada pode fazer com o documento. */
    fluxo: (id: number): Promise<FluxoDocumento> =>
      fetch(`${BASE}/documentos/${id}/fluxo`, { headers: authHeaders() }).then(handle),

    /** Na lista: quais documentos esperam uma decisão de quem está logado. */
    papel: (): Promise<{ admin: boolean; aguardandoVoce: number[] }> =>
      fetch(`${BASE}/documentos/fluxo/papel`, { headers: authHeaders() }).then(handle),

    /** Para o formulário: tipos ativos (com o próximo código) e os setores do RH. */
    opcoes: (): Promise<OpcoesDocumento> =>
      fetch(`${BASE}/documentos/opcoes`, { headers: authHeaders() }).then(handle),

    /** Configuração completa (só administrador): tipos, quem revisa e aprova, pessoas do RH. */
    config: (): Promise<ConfigDocumentos> =>
      fetch(`${BASE}/documentos/config`, { headers: authHeaders() }).then(handle),

    /** Grava a configuração inteira de uma vez (só administrador). */
    salvarConfig: (dados: {
      tipos: TipoDocumentoConfig[];
      responsaveis: ResponsavelDocumento[];
    }): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/documentos/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** Anexa o arquivo à revisão em andamento (ou abre uma nova) — entra como rascunho. */
    anexar: (
      id: number,
      dados: { file: File | null; versao: string; descricao: string },
    ): Promise<{ ok: boolean }> => {
      const fd = new FormData();
      if (dados.file) fd.append("file", dados.file);
      fd.append("versao", dados.versao);
      fd.append("descricao", dados.descricao);
      return fetch(`${BASE}/documentos/${id}/revisao`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    /** Anda o fluxo: enviar | aprovar | publicar | devolver | descartar | obsoleto. */
    acao: (
      id: number,
      acao: AcaoDocumento,
      parecer = "",
      revisaoId: number | null = null,
    ): Promise<{ ok: boolean; mensagem: string }> =>
      fetch(`${BASE}/documentos/${id}/fluxo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ acao, parecer, revisao_id: revisaoId }),
      }).then(handle),
  },

  /** Plano de ação por IA para uma Não Conformidade. */
  planoAcao: {
    gerar: (
      ncId: number,
    ): Promise<{
      nc: { id: number; codigo: string | null; titulo: string; departamento: string | null };
      gestor: string | null;
      plano: { causa_raiz: string; acoes: PlanoAcaoItem[]; observacoes: string; fonte: "ia" | "padrao" };
    }> =>
      fetch(`${BASE}/nao_conformidades/${ncId}/plano-acao`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    aplicar: (
      ncId: number,
      acoes: PlanoAcaoItem[],
      causaRaiz?: string,
    ): Promise<{ ok: boolean; criadas: number }> =>
      fetch(`${BASE}/nao_conformidades/${ncId}/plano-acao/aplicar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ acoes, causa_raiz: causaRaiz ?? "" }),
      }).then(handle),
  },

  /**
   * Satisfação e retrabalho DA EXECUÇÃO (por serviço, colaborador e equipe),
   * lidos da Gestão da Operação/Planejamento. Complementa — não substitui — os
   * indicadores de departamento registrados aqui.
   */
  desempenho: (): Promise<Desempenho> =>
    fetch(`${BASE}/desempenho`, { headers: authHeaders() }).then(handle),

  /** Gestores por setor: cadastro vindo do RH + avaliação da Qualidade. */
  gestores: {
    list: (): Promise<{ itens: GestorSetor[]; total: number; fonte: string }> =>
      fetch(`${BASE}/gestores-setor`, { headers: authHeaders() }).then(handle),

    avaliar: (data: {
      rh_id: number;
      gestor: string;
      departamento: string;
      situacao: string;
      observacao: string;
    }): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/gestores-setor/avaliacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),
  },
};

/** O que dá para fazer com um documento no fluxo de aprovação. */
export type AcaoDocumento = "enviar" | "aprovar" | "publicar" | "devolver" | "descartar" | "obsoleto";

/** Uma revisão do documento (o arquivo e as assinaturas). */
export interface RevisaoDocumento {
  id: number;
  versao: string | null;
  descricao: string | null;
  arquivo_nome: string | null;
  tem_arquivo: boolean;
  /** Rascunho | Aguardando revisão | Aguardando aprovação | Aguardando publicação | Devolvido | Vigente | Obsoleta | Descartada */
  etapa: string;
  elaborado_nome: string | null;
  enviado_em: string | null;
  /** Desde 22/10/2026 é quem elaborou (a revisão faz parte da elaboração). */
  revisado_nome: string | null;
  revisado_em: string | null;
  /** Revisões de antes de 22/10/2026: o administrador revisou no lugar de quem revisava o setor. */
  revisado_substituto: boolean;
  aprovado_nome: string | null;
  aprovado_em: string | null;
  /** O administrador aprovou no lugar de quem a configuração manda aprovar. */
  aprovado_substituto: boolean;
  /** Quem publicou (colaborador da Qualidade) — a partir daí a revisão vale. */
  publicado_nome: string | null;
  publicado_em: string | null;
  /** O administrador publicou no lugar de quem a configuração manda publicar. */
  publicado_substituto: boolean;
  created_at: string;
}

/** Um passo do histórico do documento. */
export interface EventoDocumento {
  id: number;
  revisao_id: number | null;
  versao: string | null;
  acao: string;
  etapa: string | null;
  usuario_nome: string | null;
  observacao: string | null;
  created_at: string;
}

export interface FluxoDocumento {
  documento: {
    id: number;
    codigo: string;
    titulo: string | null;
    departamento: string | null;
    status: string | null;
    versao: string | null;
    data_revisao: string | null;
    etapa_aprovacao: string | null;
    /** Quem foi escolhido no cadastro para aprovar (null = documento de antes). */
    aprovador: string | null;
  };
  /** A revisão que está andando (no máximo uma). */
  emAndamento: RevisaoDocumento | null;
  /** A versão que vale hoje. */
  vigente: RevisaoDocumento | null;
  revisoes: RevisaoDocumento[];
  eventos: EventoDocumento[];
  /** Quem aprova este documento — o escolhido no cadastro (vazio = o administrador). */
  aprovadores: Decisor[];
  /** Quem publica os documentos deste setor (vazio = o administrador). */
  publicadores: Decisor[];
  /** De onde veio quem aprova: escolhido no cadastro, marcados no setor, no Padrão, regra do RH ou administrador. */
  origemAprovacao: OrigemDecisor;
  origemPublicacao: OrigemDecisor;
  /** Revisão periódica da versão vigente (null = o tipo não vence ou ainda não há vigente). */
  validade: Validade | null;
  /** Rótulo sugerido para uma revisão nova. */
  proximaVersao: string;
  pode: {
    anexar: boolean;
    enviar: boolean;
    aprovar: boolean;
    aprovarNoLugar: boolean;
    publicar: boolean;
    publicarNoLugar: boolean;
    descartar: boolean;
    tornarObsoleto: boolean;
  };
}

/** Quem aprova ou publica — como no cadastro do RH. */
export interface Decisor {
  nome: string;
  email: string | null;
}

export type OrigemDecisor = "escolhido" | "setor" | "padrao" | "rh" | "admin";

export interface Validade {
  /** AAAA-MM-DD */
  proxima: string;
  /** Dias até a próxima revisão (negativo = vencida). */
  dias: number;
  situacao: "vencida" | "vencendo" | "em_dia";
  meses: number;
}

/** Tipo de documento como o formulário usa. */
export interface TipoDocumentoOpcao {
  nome: string;
  sigla: string;
  revisar_meses: number | null;
  /** O código que o próximo documento deste tipo vai ganhar (ex.: PR-004). */
  proximo: string;
}

export interface OpcoesDocumento {
  tipos: TipoDocumentoOpcao[];
  /** Setores do RH. */
  setores: string[];
  /** Pessoas ativas no RH — a lista do campo "Quem aprova". */
  pessoas: { nome: string; cargo: string | null; departamento: string | null }[];
  /** Sugeridos para aprovar, por setor ("" = setor ainda não escolhido). */
  aprovadoresPorSetor: Record<string, string[]>;
  rhOnline: boolean;
  avisoDias: number;
}

/** Tipo de documento no pop-up "Configurar" (id ausente = tipo novo). */
export interface TipoDocumentoConfig {
  id?: number;
  nome: string;
  sigla: string;
  revisar_meses: number | null;
  ativo: boolean;
  /** Quantos documentos usam este tipo (com algum, não dá para remover). */
  documentos?: number;
  proximo?: string;
}

/**
 * Uma pessoa marcada para aprovar (sugerida no cadastro) ou publicar os
 * documentos de um setor ("*" = Padrão). "revisor" sobrou de antes de
 * 22/10/2026: a tela não mostra mais, mas devolve como veio ao salvar.
 */
export interface ResponsavelDocumento {
  departamento: string;
  papel: "revisor" | "aprovador" | "publicador";
  rh_id: number | null;
  nome: string;
  email: string | null;
}

/** Colaborador do RH que pode ser marcado. */
export interface PessoaRh {
  rh_id: number;
  nome: string;
  cargo: string | null;
  departamento: string | null;
  email: string | null;
  ativo: boolean;
}

export interface SetorConfig {
  nome: string;
  /** Setor usado em documentos ou na configuração, mas que o RH não tem. */
  foraDoRh: boolean;
  documentos: number;
  /** Gestores do setor no RH (sugeridos para aprovar, com os diretores, se ninguém for marcado). */
  gestoresRh: string[];
}

export interface ConfigDocumentos {
  tipos: TipoDocumentoConfig[];
  responsaveis: ResponsavelDocumento[];
  setores: SetorConfig[];
  pessoas: PessoaRh[];
  /** Diretores ativos no RH (aprovam quando ninguém foi marcado). */
  diretoresRh: string[];
  /** Colaboradores ativos do setor Qualidade no RH (publicam quando ninguém foi marcado). */
  qualidadeRh: string[];
  rhOnline: boolean;
  avisoDias: number;
}

/** Uma avaliação registrada pela Qualidade. */
export interface AvaliacaoGestor {
  id: number;
  departamento: string;
  gestor: string;
  situacao: string; // Conforme | Não conformidade
  observacao: string | null;
  avaliador: string | null;
  data: string;
}

/** Gestor de setor: cadastro do RH + a última avaliação da Qualidade. */
export interface GestorSetor {
  rh_id: number;
  nome: string;
  cargo: string | null;
  departamento: string | null;
  email: string | null;
  telefone: string | null;
  status: string | null;
  situacao: string | null;
  observacao: string | null;
  avaliadoEm: string | null;
  avaliador: string | null;
  historico: AvaliacaoGestor[];
}

/** Média de satisfação e retrabalho de um colaborador na execução. */
export interface DesempenhoColaborador {
  id: number;
  nome: string;
  equipe: string | null;
  cargo: string | null;
  satisfacaoQtd: number;
  satisfacaoMedia: number | null;
  satisfacaoUltima: string | null;
  retrabalhoOcorrencias: number;
  retrabalhoQtd: number;
  retrabalhoCusto: number;
  retrabalhoAbertos: number;
}

/** O mesmo, consolidado por equipe (setor). */
export interface DesempenhoEquipe {
  equipe: string;
  colaboradores: number;
  satisfacaoQtd: number;
  satisfacaoMedia: number | null;
  retrabalhoOcorrencias: number;
  retrabalhoCusto: number;
}

/** Uma avaliação de serviço executado. */
export interface ServicoAvaliado {
  id: number;
  data: string | null;
  cliente: string | null;
  obra: string | null;
  responsavel: string | null;
  equipe: string | null;
  nota: number | null;
  nivel: string | null;
  comentario: string | null;
}

/** Um retrabalho registrado na execução. */
export interface RetrabalhoExecucao {
  id: number;
  data: string | null;
  obra: string | null;
  responsavel: string | null;
  equipe: string | null;
  descricao: string | null;
  motivo: string | null;
  quantidade: number;
  custo: number;
  status: string;
}

export interface Desempenho {
  porColaborador: DesempenhoColaborador[];
  porEquipe: DesempenhoEquipe[];
  servicos: ServicoAvaliado[];
  retrabalhos: RetrabalhoExecucao[];
}
